import type { Event, View } from "react-big-calendar"
import { dateFnsLocalizer, Views } from "react-big-calendar"
import {
    format,
    parse,
    getDay,
    addHours,
    differenceInHours,
    // eslint-disable-next-line import/no-duplicates -- This is a known date-fns bug
} from "date-fns"
// eslint-disable-next-line import/no-duplicates -- This is a known date-fns bug
import { enUS } from "date-fns/locale"
import { zonedTimeToUtc } from "date-fns-tz"

import type { MinimalCalendarEventFieldsFragment } from "~graphql/calendar-events.generated"
import { getStartOfWeek } from "~utils/helpers/date-helpers"

export const ALL_VIEWS = [Views.MONTH, Views.WEEK, Views.AGENDA]
export const AVAILABILITY_VIEWS = [Views.MONTH, Views.WEEK]
/**
 * Edit availability when the UI is month-grid only: toolbar has no view toggles
 * (calendar stays on month via Big Calendar config). Week eligibility still uses
 * `availabilityViews.includes(Views.WEEK)` — empty means week/hourly branch is off.
 */
const EDIT_AVAILABILITY_NO_TOOLBAR_VIEW_OPTIONS: View[] = []

function getAvailabilityViewsForResource(
    supportsHourlyAvailability: boolean
): View[] {
    return supportsHourlyAvailability
        ? [Views.WEEK]
        : EDIT_AVAILABILITY_NO_TOOLBAR_VIEW_OPTIONS
}

export function getShouldShowHourlyWeekLock({
    canAccessHourlyCalendar,
    supportsHourlyAvailability,
}: {
    canAccessHourlyCalendar: boolean
    supportsHourlyAvailability: boolean
}) {
    return canAccessHourlyCalendar && !!supportsHourlyAvailability
}

export function getAvailabilityViewsForToolbar({
    isEditPage,
    canAccessHourlyCalendar,
    supportsHourlyAvailability,
}: {
    isEditPage: boolean
    canAccessHourlyCalendar: boolean
    supportsHourlyAvailability: boolean
}): View[] {
    if (!isEditPage) return ALL_VIEWS
    if (!canAccessHourlyCalendar) {
        return EDIT_AVAILABILITY_NO_TOOLBAR_VIEW_OPTIONS
    }
    return getAvailabilityViewsForResource(supportsHourlyAvailability)
}

// TYPES
export type CalendarEventType = MinimalCalendarEventFieldsFragment & Event

export function getFormattedEventsForCalendar(
    calendarEvents: MinimalCalendarEventFieldsFragment[]
): CalendarEventType[] {
    return calendarEvents.map((calendarEvent) => {
        const [start, end] = getCheckInCheckOutWithOffset(
            calendarEvent.start_date,
            calendarEvent.end_date,
            calendarEvent.time_zone
        )

        return { ...calendarEvent, title: calendarEvent.title, start, end }
    })
}

/**
 * `HourlyWeeklyCalendar` (@mallardbay/lib-react-components) positions events using
 * `start_date` / `end_date` (UTC ms) as plain Dates in the viewer's local zone.
 * Shift those timestamps with the same offset as `getFormattedEventsForCalendar`
 * so wall-clock matches `event.time_zone`. Prefer fixing this in lib long-term.
 *
 * Safe to call on events already processed by `getFormattedEventsForCalendar`:
 * that helper keeps original `start_date` / `end_date` from the API while adding
 * `start` / `end`, so this mapping still applies the same offset as for raw events.
 */
export function getEventsWithTimezoneOffsetApplied(
    calendarEvents: MinimalCalendarEventFieldsFragment[]
): CalendarEventType[] {
    return calendarEvents.map((calendarEvent) => {
        const [start, end] = getCheckInCheckOutWithOffset(
            calendarEvent.start_date,
            calendarEvent.end_date,
            calendarEvent.time_zone
        )

        return {
            ...calendarEvent,
            title: calendarEvent.title,
            start,
            end,
            start_date: start.getTime(),
            end_date: end.getTime(),
        }
    })
}

export const localizer = dateFnsLocalizer({
    format,
    parse,
    getDay,
    startOfWeek: () => getStartOfWeek(),
    locales: { "en-Us": enUS },
})

/*
    react-big-calendar does not play very nicely with time zones, this will fix that
    by adjusting the dates by the offset of the time zone.
    Please see: https://github.com/jquense/react-big-calendar/issues/118
*/
export function getCheckInCheckOutWithOffset(
    checkInTime: Date | number,
    checkOutTime: Date | number,
    timeZone: string
): [Date, Date] {
    // Get the time zoned check in
    const zonedStart = zonedTimeToUtc(checkInTime, timeZone)

    // Calculate the offset (moving in opposite direction of the delta)
    const offset = differenceInHours(zonedStart, checkInTime) * -1

    // Adjust check in and check out by the offset
    const checkIn = addHours(checkInTime, offset)
    const checkOut = addHours(checkOutTime, offset)

    return [checkIn, checkOut]
}

export function getIsWeekView(view: View) {
    return view === Views.WEEK
}
